import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Fade,
  Skeleton,
  Paper,
  IconButton,
  Tooltip,
  TextField,
  TablePagination,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { format, isSameDay } from 'date-fns';
import { Refresh, Print, Search as SearchIcon } from '@mui/icons-material';
import InputAdornment from '@mui/material/InputAdornment';
import { api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { DailyCollection, getStartOfWeek, parseDateValue, getCollectionStatus, fmtCurrency, computeRemainingBalance } from './types';
import CollectionTable from './CollectionTable';
import CollectionCardList from './CollectionCardList';
import PaymentDialog from './PaymentDialog';
import OrderDialog from './OrderDialog';
import ReturnDialog from './ReturnDialog';
import UndoPayment from '../UndoPayment';
import ReschedulePayment from '../ReschedulePayment';

export default function CollectionsPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const collectorParam = searchParams.get('collector');
  const [collections, setCollections] = useState<DailyCollection[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedWeek, setSelectedWeek] = useState<Date>(() => getStartOfWeek(new Date()));
  const [loading, setLoading] = useState(false);
  const [viewType, setViewType] = useState<'daily' | 'weekly'>('daily');
  const [statusFilter, setStatusFilter] = useState<'all' | 'paid' | 'pending'>('all');
  const [collectorFilter, setCollectorFilter] = useState<string>(collectorParam || 'all');
  const [collectorTabs, setCollectorTabs] = useState<string[]>([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [paymentDialog, setPaymentDialog] = useState<{ open: boolean; collection: DailyCollection | null }>({
    open: false, collection: null,
  });
  const [rescheduleDialog, setRescheduleDialog] = useState({
    open: false, saleId: 0, installmentNumber: 0, customerName: '', currentDueDate: new Date(), remainingBalance: undefined as number | undefined,
  });
  const [undoPaymentDialog, setUndoPaymentDialog] = useState<{ open: boolean; payment: any }>({
    open: false, payment: null,
  });
  const [orderDialog, setOrderDialog] = useState<{ open: boolean; customerId: number | null }>({
    open: false, customerId: null,
  });
  const [returnDialog, setReturnDialog] = useState<{ open: boolean; collection: DailyCollection | null }>({
    open: false, collection: null,
  });
  const [customers, setCustomers] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [clientSearch, setClientSearch] = useState('');

  const weekHelperText = useMemo(() => {
    const wednesday = getStartOfWeek(selectedWeek);
    const tuesday = new Date(wednesday);
    tuesday.setDate(wednesday.getDate() + 6);
    return `Semana: ${format(wednesday, 'dd/MM/yyyy')} - ${format(tuesday, 'dd/MM/yyyy')}`;
  }, [selectedWeek]);

  useEffect(() => {
    fetchCollections();
    fetchCustomers();
  }, [selectedDate, selectedWeek, viewType]);

  useEffect(() => {
    if (user?.role === 'admin') {
      api.get('/collections/collectors').then(res => setCollectorTabs(res.data)).catch(() => {});
    }
  }, [user?.role]);

  const fetchCollections = async () => {
    setLoading(true);
    try {
      let data: DailyCollection[] = [];
      if (viewType === 'daily') {
        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        const response = await api.get('/collections/daily', { params: { date: dateStr } });
        data = response.data || [];
      } else {
        const dateStr = format(selectedWeek, 'yyyy-MM-dd');
        const response = await api.get('/collections/weekly', { params: { date: dateStr } });
        data = response.data || [];
      }
      setCollections(data);
    } catch (error) {
      console.error('Error fetching collections:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers');
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const handleStatusFilterChange = (filter: 'all' | 'paid' | 'pending') => {
    setStatusFilter(prev => (prev === filter ? 'all' : filter));
    setPage(0);
  };

  const handlePayment = (collection: DailyCollection) => {
    setPaymentDialog({ open: true, collection });
  };

  // Adelantar próxima cuota: el cobrador cobra hoy una cuota más allá de la
  // que correspondería este día. Pisa el anchor de la fila con la próxima
  // cuota impaga y abre el PaymentDialog con esa.
  const handleAdvanceNext = async (collection: DailyCollection) => {
    try {
      const res = await api.get(`/sales/${collection.sale_id}/installments`);
      const installments: any[] = res.data?.installments || [];
      const next = installments
        .filter((i: any) => !i.is_paid && i.installment_number > collection.installment_number)
        .sort((a: any, b: any) => a.installment_number - b.installment_number)[0];
      if (!next) return;

      const advancedCollection: DailyCollection = {
        ...collection,
        installment_number: next.installment_number,
        installment_amount: next.current_amount ?? collection.installment_amount,
        due_date: next.due_date ?? collection.due_date,
        is_paid: false,
        is_partial: !!next.is_partial,
        is_rescheduled: !!next.is_rescheduled,
        total_paid_amount: next.total_paid_amount ?? 0,
        remaining_balance: next.remaining_balance ?? next.current_amount ?? collection.installment_amount,
        payment_id: undefined,
        payments_count: 0,
      };
      setPaymentDialog({ open: true, collection: advancedCollection });
    } catch (e) {
      console.error('Error fetching installments for advance:', e);
    }
  };

  const handleReschedule = (collection: DailyCollection) => {
    const dueDate = parseDateValue(collection.due_date);
    setRescheduleDialog({
      open: true,
      saleId: collection.sale_id,
      installmentNumber: collection.installment_number,
      customerName: `${collection.customer_surname} ${collection.customer_name}`,
      currentDueDate: dueDate,
      remainingBalance: collection.is_partial ? collection.remaining_balance : undefined,
    });
  };

  const handleReturn = (collection: DailyCollection) => {
    setReturnDialog({ open: true, collection });
  };

  const handleUndoPayment = (collection: DailyCollection) => {
    // Si la fila está como Cobrada, usar payment_id (pago de la cuota mostrada).
    // Si está Pendiente pero la venta tiene un pago previo (caso prepago),
    // usar last_sale_payment_id para deshacer el último cobro hecho.
    const paymentId = collection.payment_id ?? collection.last_sale_payment_id;
    if (!paymentId) return;
    setUndoPaymentDialog({
      open: true,
      payment: {
        id: paymentId,
        amount: collection.total_paid_amount ?? collection.installment_amount,
        customer_name: collection.customer_name,
        customer_surname: collection.customer_surname,
        sale_id: collection.sale_id,
        installment_number: collection.installment_number,
        payment_date: collection.payment_date ?? collection.last_sale_payment_date,
      },
    });
  };

  // Filter collections — el backend ya filtra por target_date (daily/weekly).
  // No re-filtrar por due_date acá: las filas pueden tener due_date != target_date
  // (cuotas atrasadas con due en el pasado, o cuotas cobradas hoy cuya due era futura).
  const dateFilteredCollections = collections;

  const collectorFilteredCollections = dateFilteredCollections.filter((c) => {
    if (collectorFilter === 'all') return true;
    return (c.collector_name || '').toLowerCase() === collectorFilter.toLowerCase();
  });

  const statusFilteredCollections = collectorFilteredCollections.filter((c) => {
    if (statusFilter === 'paid') return c.is_paid;
    if (statusFilter === 'pending') return !c.is_paid;
    return true;
  });

  const filteredCollections = statusFilteredCollections.filter((c) => {
    if (!clientSearch.trim()) return true;
    const search = clientSearch.trim().toLowerCase();
    const fullName = `${c.customer_surname} ${c.customer_name}`.toLowerCase();
    return fullName.includes(search);
  });

  const paginatedCollections = useMemo(() => filteredCollections.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [filteredCollections, page, rowsPerPage]);

  // Recalculate summary based on collector filter
  const computedSummary = useMemo(() => {
    const data = collectorFilteredCollections;
    const total = data.length;
    const paid = data.filter((item) => item.is_paid).length;
    const amountCollected = data.reduce((sum, item) => {
      if (!item.is_paid) return sum;
      const amount = typeof item.total_paid_amount === 'number'
        ? item.total_paid_amount
        : item.installment_amount;
      return sum + (Number(amount) || 0);
    }, 0);
    return { total, paid, pending: total - paid, amountCollected };
  }, [collectorFilteredCollections]);

  // Mostrar el HTML del reporte: en desktop abre ventana e imprime;
  // en mobile (donde el popup blocker suele bloquear window.open)
  // descarga el archivo .html para que el usuario lo abra desde el celu
  // y lo comparta/imprima desde ahí.
  const presentReport = (html: string, filename: string) => {
    if (!isMobile) {
      const w = window.open('', '_blank');
      if (w) {
        w.document.write(html);
        w.document.close();
        w.focus();
        setTimeout(() => w.print(), 400);
        return;
      }
    }
    // Mobile fallback: descargar
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const printWeeklyReport = () => {
    const wednesday = getStartOfWeek(selectedWeek);
    const tuesday = new Date(wednesday);
    tuesday.setDate(wednesday.getDate() + 6);

    const dayNames = ['Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo', 'Lunes', 'Martes'];
    const daysByOffset: Record<number, DailyCollection[]> = {};
    for (let i = 0; i < 7; i++) daysByOffset[i] = [];

    filteredCollections.forEach((c) => {
      const due = parseDateValue(c.due_date);
      const diff = Math.round((due.getTime() - wednesday.getTime()) / (1000 * 60 * 60 * 24));
      if (diff >= 0 && diff < 7) daysByOffset[diff].push(c);
    });

    const collectorLabel = collectorFilter === 'all' ? 'Todos los cobradores' : collectorFilter;
    const rangeLabel = `${format(wednesday, 'dd/MM/yyyy')} – ${format(tuesday, 'dd/MM/yyyy')}`;

    let tablesHtml = '';
    for (let i = 0; i < 7; i++) {
      const items = daysByOffset[i];
      const dayDate = new Date(wednesday);
      dayDate.setDate(wednesday.getDate() + i);
      const dateStr = format(dayDate, 'dd/MM/yyyy');

      tablesHtml += `<h2 style="margin:18px 0 6px;font-size:15px;border-bottom:2px solid #333;padding-bottom:4px;">${dayNames[i]} ${dateStr}</h2>`;
      if (items.length === 0) {
        tablesHtml += '<p style="color:#888;font-size:12px;margin:4px 0 12px;">Sin cobros programados</p>';
        continue;
      }

      tablesHtml += `<table style="width:100%;border-collapse:collapse;font-size:12px;margin-bottom:12px;">
        <thead><tr style="background:#f0f0f0;">
          <th style="border:1px solid #ccc;padding:4px 6px;text-align:left;">Cliente</th>
          <th style="border:1px solid #ccc;padding:4px 6px;text-align:left;">Dirección</th>
          <th style="border:1px solid #ccc;padding:4px 6px;text-align:left;">Producto</th>
          <th style="border:1px solid #ccc;padding:4px 6px;text-align:center;">Cuota</th>
          <th style="border:1px solid #ccc;padding:4px 6px;text-align:center;">Debe</th>
          <th style="border:1px solid #ccc;padding:4px 6px;text-align:right;">Monto</th>
          <th style="border:1px solid #ccc;padding:4px 6px;text-align:right;">Saldo</th>
          <th style="border:1px solid #ccc;padding:4px 6px;text-align:center;">Estado</th>
        </tr></thead><tbody>`;

      items.forEach((c) => {
        const status = getCollectionStatus(c);
        const remaining = computeRemainingBalance(c);
        const overdueCount = c.overdue_count ?? 0;
        const statusColor = status.label === 'Cobrado' ? '#16a34a'
          : status.label === 'Vencido' ? '#dc2626'
          : status.label === 'Reprogramado' ? '#6b7280'
          : status.label === 'Pago parcial' ? '#2563eb'
          : '#d97706';
        tablesHtml += `<tr>
          <td style="border:1px solid #ccc;padding:4px 6px;">${c.customer_surname} ${c.customer_name}</td>
          <td style="border:1px solid #ccc;padding:4px 6px;">${c.customer_address || ''}</td>
          <td style="border:1px solid #ccc;padding:4px 6px;">${c.product_name}</td>
          <td style="border:1px solid #ccc;padding:4px 6px;text-align:center;">${c.installment_number}/${c.total_installments}</td>
          <td style="border:1px solid #ccc;padding:4px 6px;text-align:center;${overdueCount > 0 ? 'font-weight:bold;color:#dc2626;' : ''}">${overdueCount > 0 ? overdueCount : '-'}</td>
          <td style="border:1px solid #ccc;padding:4px 6px;text-align:right;">${fmtCurrency(c.installment_amount)}</td>
          <td style="border:1px solid #ccc;padding:4px 6px;text-align:right;">${remaining > 0 && remaining < c.installment_amount ? fmtCurrency(remaining) : ''}</td>
          <td style="border:1px solid #ccc;padding:4px 6px;text-align:center;font-weight:bold;color:${statusColor};">${status.label.toUpperCase()}</td>
        </tr>`;
      });
      tablesHtml += '</tbody></table>';
    }

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
      <title>Planilla Semanal – ${collectorLabel}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; color: #222; }
        h1 { font-size: 18px; margin-bottom: 2px; }
        .subtitle { font-size: 13px; color: #555; margin-bottom: 16px; }
        @media print { body { margin: 10px; } }
      </style>
    </head><body>
      <h1>Planilla Semanal de Cobros</h1>
      <div class="subtitle"><strong>Cobrador:</strong> ${collectorLabel} &nbsp;|&nbsp; <strong>Semana:</strong> ${rangeLabel}</div>
      ${tablesHtml}
    </body></html>`;

    const safeCollector = collectorLabel.replace(/[^a-zA-Z0-9_-]+/g, '_');
    presentReport(html, `planilla_semanal_${safeCollector}_${format(wednesday, 'yyyy-MM-dd')}.html`);
  };

  const printDailyReport = () => {
    const dateStr = format(selectedDate, 'dd/MM/yyyy');
    const collectorLabel = collectorFilter === 'all' ? 'Todos los cobradores' : collectorFilter;

    let tableHtml = `<table style="width:100%;border-collapse:collapse;font-size:12px;margin-bottom:12px;">
      <thead><tr style="background:#f0f0f0;">
        <th style="border:1px solid #ccc;padding:4px 6px;text-align:left;">Cliente</th>
        <th style="border:1px solid #ccc;padding:4px 6px;text-align:left;">Direccion</th>
        <th style="border:1px solid #ccc;padding:4px 6px;text-align:left;">Producto</th>
        <th style="border:1px solid #ccc;padding:4px 6px;text-align:center;">Cuota</th>
        <th style="border:1px solid #ccc;padding:4px 6px;text-align:center;">Debe</th>
        <th style="border:1px solid #ccc;padding:4px 6px;text-align:right;">Monto</th>
        <th style="border:1px solid #ccc;padding:4px 6px;text-align:right;">Saldo</th>
        <th style="border:1px solid #ccc;padding:4px 6px;text-align:center;">Estado</th>
      </tr></thead><tbody>`;

    filteredCollections.forEach((c) => {
      const status = getCollectionStatus(c);
      const remaining = computeRemainingBalance(c);
      const overdueCount = c.overdue_count ?? 0;
      const statusColor = status.label === 'Cobrado' ? '#16a34a'
        : status.label === 'Vencido' ? '#dc2626'
        : status.label === 'Reprogramado' ? '#6b7280'
        : status.label === 'Pago parcial' ? '#2563eb'
        : '#d97706';
      tableHtml += `<tr>
        <td style="border:1px solid #ccc;padding:4px 6px;">${c.customer_surname} ${c.customer_name}</td>
        <td style="border:1px solid #ccc;padding:4px 6px;">${c.customer_address || ''}</td>
        <td style="border:1px solid #ccc;padding:4px 6px;">${c.product_name}</td>
        <td style="border:1px solid #ccc;padding:4px 6px;text-align:center;">${c.installment_number}/${c.total_installments}</td>
        <td style="border:1px solid #ccc;padding:4px 6px;text-align:center;${overdueCount > 0 ? 'font-weight:bold;color:#dc2626;' : ''}">${overdueCount > 0 ? overdueCount : '-'}</td>
        <td style="border:1px solid #ccc;padding:4px 6px;text-align:right;">${fmtCurrency(c.installment_amount)}</td>
        <td style="border:1px solid #ccc;padding:4px 6px;text-align:right;">${remaining > 0 && remaining < c.installment_amount ? fmtCurrency(remaining) : ''}</td>
        <td style="border:1px solid #ccc;padding:4px 6px;text-align:center;font-weight:bold;color:${statusColor};">${status.label.toUpperCase()}</td>
      </tr>`;
    });
    tableHtml += '</tbody></table>';

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
      <title>Planilla Diaria – ${collectorLabel}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; color: #222; }
        h1 { font-size: 18px; margin-bottom: 2px; }
        .subtitle { font-size: 13px; color: #555; margin-bottom: 16px; }
        @media print { body { margin: 10px; } }
      </style>
    </head><body>
      <h1>Planilla Diaria de Cobros</h1>
      <div class="subtitle"><strong>Cobrador:</strong> ${collectorLabel} &nbsp;|&nbsp; <strong>Fecha:</strong> ${dateStr}</div>
      ${filteredCollections.length === 0 ? '<p style="color:#888;">Sin cobros programados</p>' : tableHtml}
    </body></html>`;

    const safeCollector = collectorLabel.replace(/[^a-zA-Z0-9_-]+/g, '_');
    presentReport(html, `planilla_diaria_${safeCollector}_${format(selectedDate, 'yyyy-MM-dd')}.html`);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Container maxWidth="lg" sx={{ mt: { xs: 1, sm: 2 }, mb: { xs: 1, sm: 2 }, px: { xs: 1, sm: 2 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
          <Typography
            variant="h4"
            sx={{ fontSize: { xs: '1.3rem', sm: '1.7rem' }, fontWeight: 800, color: 'text.primary' }}
          >
            Cobranzas
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {viewType === 'daily' && (
              <Tooltip title="Imprimir planilla diaria">
                <IconButton
                  onClick={printDailyReport}
                  sx={{
                    width: 40, height: 40,
                    backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                    '&:hover': { backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)' },
                  }}
                >
                  <Print sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}
            {viewType === 'weekly' && (
              <Tooltip title="Imprimir planilla semanal">
                <IconButton
                  onClick={printWeeklyReport}
                  sx={{
                    width: 40, height: 40,
                    backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                    '&:hover': { backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)' },
                  }}
                >
                  <Print sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Actualizar datos">
              <IconButton
                onClick={fetchCollections}
                sx={{
                  width: 40, height: 40,
                  backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                  '&:hover': { backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)' },
                }}
              >
                <Refresh sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Tabs */}
        <Box sx={{ mb: { xs: 2, sm: 3 }, borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={viewType}
            onChange={(_, v) => { setViewType(v); setPage(0); }}
            sx={{
              '& .MuiTab-root': { fontSize: { xs: '0.875rem', sm: '1rem' }, minHeight: { xs: 44, sm: 48 }, fontWeight: 500, textTransform: 'none' },
              '& .MuiTabs-indicator': { background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', height: 3, borderRadius: '2px 2px 0 0' },
            }}
          >
            <Tab label="Vista Diaria" value="daily" />
            <Tab label="Vista Semanal" value="weekly" />
          </Tabs>
        </Box>

        {/* Collector tabs (admin only) */}
        {user?.role === 'admin' && collectorTabs.length > 0 && (
          <Box sx={{ mb: { xs: 2, sm: 3 }, borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={collectorFilter}
              onChange={(_, v) => { setCollectorFilter(v); setPage(0); }}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': { fontSize: { xs: '0.8rem', sm: '0.9rem' }, minHeight: { xs: 36, sm: 40 }, fontWeight: 500, textTransform: 'none' },
                '& .MuiTabs-indicator': { background: 'linear-gradient(135deg, #10B981, #059669)', height: 3, borderRadius: '2px 2px 0 0' },
              }}
            >
              <Tab label="Todos" value="all" />
              {collectorTabs.map((name) => (
                <Tab key={name} label={name} value={name} />
              ))}
            </Tabs>
          </Box>
        )}

        {/* Date picker */}
        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
          {viewType === 'daily' ? (
            <DatePicker
              label="Seleccionar Dia"
              value={selectedDate}
              onChange={(d) => d && setSelectedDate(d)}
              slots={{ textField: TextField }}
              slotProps={{ textField: { fullWidth: isMobile, size: isMobile ? 'small' : 'medium', sx: { minWidth: { sm: 200 } } } }}
            />
          ) : (
            <DatePicker
              label="Semana (solo miércoles)"
              value={selectedWeek}
              onChange={(d) => d && setSelectedWeek(getStartOfWeek(d))}
              shouldDisableDate={(d) => d.getDay() !== 3}
              slots={{ textField: TextField }}
              slotProps={{ textField: { fullWidth: isMobile, size: isMobile ? 'small' : 'medium', helperText: weekHelperText, sx: { minWidth: { sm: 260 } } } }}
            />
          )}
        </Box>

        {/* Client search */}
        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
          <TextField
            placeholder="Buscar por cliente..."
            size="small"
            fullWidth={isMobile}
            value={clientSearch}
            onChange={(e) => { setClientSearch(e.target.value); setPage(0); }}
            sx={{ minWidth: { sm: 280 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Summary cards - filter toggles */}
        <Grid container spacing={2} sx={{ mb: { xs: 2, sm: 3 } }}>
          <Grid item xs={6} sm={3}>
            <SummaryCard
              label="Total"
              value={computedSummary.total}
              color="#6366F1"
              active={statusFilter === 'all'}
              onClick={() => handleStatusFilterChange('all')}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryCard
              label="Pagadas"
              value={computedSummary.paid}
              color="#10B981"
              active={statusFilter === 'paid'}
              onClick={() => handleStatusFilterChange('paid')}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryCard
              label="Pendientes"
              value={computedSummary.pending}
              color="#F59E0B"
              active={statusFilter === 'pending'}
              onClick={() => handleStatusFilterChange('pending')}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryCard
              label={viewType === 'weekly' ? 'Cobrado en la semana' : 'Cobrado hoy'}
              value={`$${Math.round(computedSummary.amountCollected).toLocaleString('es-AR')}`}
              color="#0EA5E9"
            />
          </Grid>
        </Grid>

        {/* Collection list */}
        <Paper elevation={0} sx={{ p: { xs: 1, md: 2 }, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          {loading ? (
            <Box sx={{ py: 4 }}>
              <Skeleton variant="rectangular" width="100%" height={200} sx={{ borderRadius: 2 }} />
            </Box>
          ) : filteredCollections.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="textSecondary">
                {statusFilter === 'all'
                  ? 'No hay cobranzas para la fecha seleccionada'
                  : 'No hay cobranzas que coincidan con el filtro'}
              </Typography>
            </Box>
          ) : isMobile ? (
            <CollectionCardList
              collections={paginatedCollections}
              viewType={viewType}
              onPayment={handlePayment}
              onReschedule={handleReschedule}
              onUndoPayment={handleUndoPayment}
              onReturn={handleReturn}
              onAdvanceNext={handleAdvanceNext}
            />
          ) : (
            <CollectionTable
              collections={paginatedCollections}
              viewType={viewType}
              onPayment={handlePayment}
              onReschedule={handleReschedule}
              onUndoPayment={handleUndoPayment}
              onReturn={handleReturn}
              onAdvanceNext={handleAdvanceNext}
            />
          )}
          {filteredCollections.length > 0 && (
            <TablePagination
              component="div"
              count={filteredCollections.length}
              page={Math.min(page, Math.max(0, Math.ceil(filteredCollections.length / rowsPerPage) - 1))}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50]}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              labelRowsPerPage="Filas:"
            />
          )}
        </Paper>

        {/* Dialogs */}
        <PaymentDialog
          open={paymentDialog.open}
          collection={paymentDialog.collection}
          paymentDate={viewType === 'daily' ? format(selectedDate, 'yyyy-MM-dd') : undefined}
          onClose={() => setPaymentDialog({ open: false, collection: null })}
          onSuccess={() => {
            setPaymentDialog({ open: false, collection: null });
            fetchCollections();
          }}
        />

        <OrderDialog
          open={orderDialog.open}
          customerId={orderDialog.customerId}
          customers={customers}
          onClose={() => setOrderDialog({ open: false, customerId: null })}
          onSuccess={() => {
            setOrderDialog({ open: false, customerId: null });
          }}
        />

        <ReschedulePayment
          open={rescheduleDialog.open}
          onClose={() => setRescheduleDialog({ ...rescheduleDialog, open: false })}
          onSuccess={() => {
            setRescheduleDialog({ ...rescheduleDialog, open: false });
            fetchCollections();
          }}
          saleId={rescheduleDialog.saleId}
          installmentNumber={rescheduleDialog.installmentNumber}
          customerName={rescheduleDialog.customerName}
          currentDueDate={rescheduleDialog.currentDueDate}
          remainingBalance={rescheduleDialog.remainingBalance}
        />

        <UndoPayment
          open={undoPaymentDialog.open}
          onClose={() => setUndoPaymentDialog({ ...undoPaymentDialog, open: false })}
          onSuccess={() => {
            setUndoPaymentDialog({ open: false, payment: null });
            fetchCollections();
          }}
          payment={undoPaymentDialog.payment}
        />

        <ReturnDialog
          open={returnDialog.open}
          collection={returnDialog.collection}
          onClose={() => setReturnDialog({ open: false, collection: null })}
          onSuccess={() => {
            setReturnDialog({ open: false, collection: null });
            fetchCollections();
          }}
        />
      </Container>
    </LocalizationProvider>
  );
}

function SummaryCard({
  label,
  value,
  color,
  active = false,
  onClick,
}: {
  label: string;
  value: number | string;
  color: string;
  active?: boolean;
  onClick?: () => void;
}) {
  const clickable = typeof onClick === 'function';
  return (
    <Fade in timeout={300}>
      <Card
        onClick={onClick}
        sx={(theme) => ({
          cursor: clickable ? 'pointer' : 'default',
          transition: 'all 0.2s',
          border: '1px solid',
          borderColor: active ? color : 'divider',
          background: theme.palette.mode === 'dark' ? 'rgba(22, 27, 46, 0.6)' : '#ffffff',
          boxShadow: active ? `0 0 0 2px ${color}40` : 'none',
          '&:hover': clickable ? { transform: 'translateY(-2px)', boxShadow: `0 4px 12px ${color}30` } : {},
        })}
      >
        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
          <Typography sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' }, fontWeight: 800, color }}>
            {value}
          </Typography>
        </CardContent>
      </Card>
    </Fade>
  );
}
